import { getTrackingData } from './utils';

import EventBus from '@core/eventbus';
import type {
  FavouriteClubUpdateEvent,
  IdpManager,
  IdpUser,
  LoginRegisterOptions,
  ProfileOptions,
} from '@shared-types/idp.types';
import { AUTH_FLOW_TYPE, LoginSources, UserEvents } from '@shared-types/idp.types';
import GoogleTagManagerHelper from '@tracking/gtm-helper';
import { language } from '@utilities/browser';
import { isEmpty, isEqual } from '@utilities/collection-utils';
import { Trace } from '@utilities/logging';
import { appendScript, waitForScript } from '@utilities/scripts';

export class IdpHandler {
  idp: IdpManager;
  inited: boolean;
  user: IdpUser;

  async init(apiKey: string, locale = language) {
    this.idp = await (window.idpScript
      ? appendScript<IdpManager>(window.idpScript, 'idp-script', 'IdpManager')
      : waitForScript<IdpManager>('IdpManager'));
    this._attachEventEmitter();

    if (apiKey && !this.inited) {
      this.idp.init({ apiKey, locale });
    }

    await this.idp?.onReady();
    this.inited = true;
  }

  get eventEmitter() {
    return this.idp?.getEventEmitter();
  }

  _attachEventEmitter() {
    this._attachEvent(UserEvents.LOGIN, this._login);
    this._attachEvent(UserEvents.REGISTER, this._register);
    this._attachEvent(UserEvents.LOGOUT, this._logout);
    this._attachEvent(UserEvents.USERCHANGE, this._updateUser);
    this._attachEvent(UserEvents.ERROR, Trace);
    this._attachEvent(UserEvents.REQUESTCLOSE, this._closeModal);
    this._attachEvent(UserEvents.FAV_CLUB_UPDATED, this._favoriteClubUpdated);
    this._attachEvent(UserEvents.FAV_TEAM_UPDATED, this._favoriteClubUpdated);
    this._attachEvent(UserEvents.FOLLOWED_CLUB_UPDATED, this._followingClubUpdated);
  }

  showSocialRegister = (caller: LoginSources = LoginSources.CONTENT, options?: LoginRegisterOptions) =>
    this.idp.showSocialRegister(options)?.on('ready', () => this._trackModal(AUTH_FLOW_TYPE.REGISTRATION, caller));

  showLogin = async (caller: LoginSources = LoginSources.CONTENT, options?: LoginRegisterOptions) => {
    if (window.useCustomAuthDialog && !options?.containerID) {
      EventBus.dispatch('openAuthDialog', AUTH_FLOW_TYPE.LOGIN);
      this._trackModal(AUTH_FLOW_TYPE.LOGIN, caller);
      return this.eventEmitter;
    }

    await this.waitIdpReady();
    return this.idp.showLogin(options)?.on('ready', () => this._trackModal(AUTH_FLOW_TYPE.LOGIN, caller));
  };

  showRegister = async (caller: LoginSources = LoginSources.CONTENT, options?: LoginRegisterOptions) => {
    if (window.useCustomAuthDialog && !options?.containerID) {
      EventBus.dispatch('openAuthDialog', AUTH_FLOW_TYPE.REGISTRATION);
      this._trackModal(AUTH_FLOW_TYPE.REGISTRATION, caller);
      return this.eventEmitter;
    }

    await this.waitIdpReady();
    return this.idp.showRegister(options)?.on('ready', () => this._trackModal(AUTH_FLOW_TYPE.REGISTRATION, caller));
  };

  showProfile = async (options: ProfileOptions = { modal: true }) => {
    await this.waitIdpReady();
    return this.idp.showProfile(options);
  };

  _closeModal = (event = {}) => {
    EventBus.dispatch('loginModalClosed', event);
  };

  _login = (event: { user?: IdpUser }) => {
    EventBus.dispatch('userLogged', UserEvents.LOGIN, event.user);
  };

  _register = (event: { user?: IdpUser }) => {
    EventBus.dispatch('userLogged', UserEvents.REGISTER, event.user);
  };

  _logout = () => {
    this.user = null;
    EventBus.dispatch('userLogged', UserEvents.LOGOUT, null);
  };

  _favoriteClubUpdated = (event: FavouriteClubUpdateEvent = {}) => {
    EventBus.dispatch('userChangedPreferredTeam', event);
  };

  _followingClubUpdated = (event = {}) => {
    EventBus.dispatch('userChangedFollowedTeam', event);
  };

  _updateUser = (event: { user?: IdpUser }) => {
    if (isEqual(this.user, event.user)) return;
    this.user = event.user;

    const elementsToHide = Array.from(document.querySelectorAll<HTMLElement>('.js-hide-on-login'));
    const elementsToShow = Array.from(document.querySelectorAll<HTMLElement>('.js-show-on-login'));
    const elementsToRemove = Array.from(document.querySelectorAll<HTMLElement>('.js-remove-on-login'));
    const elementsToAdd = Array.from(document.querySelectorAll<HTMLElement>('.js-add-on-login'));

    const isLoggedIn = !isEmpty(this.user);
    this._toggleClasses(elementsToHide, 'pk-visibility--invisible', isLoggedIn);
    this._toggleClasses(elementsToShow, 'pk-visibility--invisible', !isLoggedIn);
    this._toggleClasses(elementsToRemove, 'pk-d--none', isLoggedIn);
    this._toggleClasses(elementsToAdd, 'pk-d--none', !isLoggedIn);

    EventBus.dispatch('userLogged', UserEvents.USERCHANGE, event.user);
    const data = getTrackingData(event.user);
    GoogleTagManagerHelper.addPageMeta(data);
  };

  _toggleClasses(elements: HTMLElement[], className: string, condition: boolean): void {
    elements.forEach((element) => {
      element.classList.toggle(className, condition);
    });
  }

  _attachEvent(event: UserEvents, callback: (...args: unknown[]) => void) {
    this.eventEmitter?.on(event, (...args) => {
      Trace('Login event', ...args);
      callback(...args);
    });
  }

  _trackModal = (event: string, caller: LoginSources = LoginSources.CONTENT) =>
    GoogleTagManagerHelper.event(
      'Gigya',
      `Gigya Form Loaded - ${event}`,
      caller,
      {
        gigya_form: event,
        gigya_location: caller,
      },
      'gigya_form_loaded',
    );

  async waitIdpReady() {
    if (this.inited) {
      return this.idp;
    }

    this.idp ??= await waitForScript<IdpManager>('IdpManager');

    await this.idp.onReady();
    return this.idp;
  }

  static readonly instance = new IdpHandler();
}

const IdpHandlerInstance = IdpHandler.instance;
if (!window.skipInitIdpHandler) {
  IdpHandlerInstance.init(window.idpApiKey);
}

window.showUefaLogin = (source = LoginSources.CONTENT) => IdpHandlerInstance.showLogin(source);
window.showUefaRegister = (source = LoginSources.CONTENT) => IdpHandlerInstance.showRegister(source);

export default IdpHandlerInstance;
